import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Megaphone, Plus, MapPin, Music2, Calendar, ChevronRight, Filter } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { INSTRUMENTS } from "../lib/instruments";

function pretty(s) {
  if (!s) return "";
  const d = new Date(s + "T00:00:00");
  return d.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });
}

export default function Gigs() {
  const { user } = useAuth();
  const [tab, setTab] = useState("open");
  const [filter, setFilter] = useState({ instrument: "", location: "", date_from: "" });
  const [items, setItems] = useState([]);

  const canPost = user?.role === "booker" || user?.role === "conductor" || user?.role === "admin";

  const load = async () => {
    const params = new URLSearchParams();
    if (tab === "mine") params.set("mine", "true");
    else params.set("status", "open");
    if (filter.instrument) params.set("instrument", filter.instrument);
    if (filter.location) params.set("location", filter.location);
    if (filter.date_from) params.set("date_from", filter.date_from);
    const { data } = await api.get(`/gigs?${params.toString()}`);
    setItems(data || []);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab, filter.instrument, filter.location, filter.date_from]);

  return (
    <div className="space-y-5" data-testid="gigs-page">
      <div className="flex items-end justify-between gap-3">
        <div>
          <div className="label-overline">Gig board</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">Open gigs</h1>
          <p className="text-ink-600 text-sm mt-1">Bookers post a date and the seats they need; musicians apply.</p>
        </div>
        {canPost && (
          <Link to="/gigs/new" className="btn-primary text-sm" data-testid="new-gig-btn">
            <Plus size={16} /> Post a gig
          </Link>
        )}
      </div>

      <div className="flex gap-1 bg-ivory-100 rounded-full p-1 w-fit">
        <button onClick={() => setTab("open")} className={`rounded-full px-4 py-1.5 text-sm font-medium ${tab === "open" ? "bg-white shadow-soft text-ink-900" : "text-ink-600"}`} data-testid="gigs-tab-open">Open</button>
        {canPost && (
          <button onClick={() => setTab("mine")} className={`rounded-full px-4 py-1.5 text-sm font-medium ${tab === "mine" ? "bg-white shadow-soft text-ink-900" : "text-ink-600"}`} data-testid="gigs-tab-mine">My posts</button>
        )}
      </div>

      <div className="card-base p-4 sm:p-5">
        <div className="label-overline mb-2 inline-flex items-center gap-2"><Filter size={12} /> Filters</div>
        <div className="grid sm:grid-cols-3 gap-3">
          <select className="input-field" value={filter.instrument} onChange={(e) => setFilter({ ...filter, instrument: e.target.value })} data-testid="gigs-instrument-filter">
            <option value="">Any instrument</option>
            {INSTRUMENTS.map((i) => <option key={i} value={i}>{i}</option>)}
          </select>
          <input className="input-field" placeholder="Location" value={filter.location} onChange={(e) => setFilter({ ...filter, location: e.target.value })} data-testid="gigs-location-filter" />
          <input type="date" className="input-field" value={filter.date_from} onChange={(e) => setFilter({ ...filter, date_from: e.target.value })} data-testid="gigs-date-filter" />
        </div>
      </div>

      {items.length === 0 ? (
        <div className="card-base p-8 text-center" data-testid="gigs-empty">
          <Megaphone className="mx-auto text-sage-500" size={28} />
          <div className="mt-3 font-heading font-semibold">No gigs match those filters.</div>
          <p className="text-sm text-ink-600 mt-1">{canPost ? "Post the first one — bookers and conductors can advertise multi-seat gigs here." : "Check back soon — fixers post here when they need players."}</p>
        </div>
      ) : (
        <ul className="space-y-3">
          {items.map((g) => (
            <li key={g.id}>
              <Link to={`/gigs/${g.id}`} className="card-base p-5 hover:-translate-y-[1px] transition flex items-start justify-between gap-3" data-testid={`gig-${g.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="label-overline text-ink-500">{pretty(g.date)}</span>
                    <span className="pill bg-ivory-100 text-ink-700 capitalize">{g.event_type}</span>
                    {g.status !== "open" && <span className="pill bg-sage-100 text-sage-700 capitalize">{g.status}</span>}
                  </div>
                  <h3 className="font-heading text-lg font-semibold truncate">{g.title}</h3>
                  <div className="text-sm text-ink-600 mt-0.5 flex flex-wrap gap-3">
                    {g.ensemble_name && <span className="inline-flex items-center gap-1"><Music2 size={12} /> {g.ensemble_name}</span>}
                    {(g.venue || g.location) && <span className="inline-flex items-center gap-1"><MapPin size={12} /> {g.venue || g.location}</span>}
                    {g.fee_info && <span className="text-sage-600">{g.fee_info}</span>}
                  </div>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {(g.seats || []).map((s) => {
                      const open = (s.count_needed || 0) - (s.confirmed || 0);
                      const full = open <= 0;
                      return (
                        <span key={s.id} className={`pill ${full ? "bg-ivory-100 text-ink-500" : "bg-sage-100 text-sage-700"}`}>
                          {s.instrument} · {full ? "filled" : `${open} open`}
                          {s.applicants > 0 && !full && <span className="text-ink-500 ml-1">({s.applicants} applied)</span>}
                        </span>
                      );
                    })}
                  </div>
                  {g.my_applications && g.my_applications.length > 0 && (
                    <div className="mt-2 text-xs text-sage-700 inline-flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-sage-500" /> You've applied
                    </div>
                  )}
                </div>
                <ChevronRight size={18} className="text-ink-400 mt-1 shrink-0" />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
